import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, setDoc, doc, serverTimestamp, getDoc, updateDoc } from 'firebase/firestore';
import { fn } from '../api/functions';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUserAccess } from '../hooks/useUserAccess';
import { v4 as uuidv4 } from 'uuid';
import { STORY_TYPES, STORY_TYPE_LABELS, StoryTypeUtils } from '../types/storyTypes';

export const StoryWizardPage = () => {
  const { currentUser } = useAuth();
  const { canCreateStory, userAccess, remainingTokens } = useUserAccess();
  const navigate = useNavigate();
  const [userCurrency, setUserCurrency] = useState({ code: 'NZD', symbol: '$' });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    additionalContext: '',
    // Enhanced fields for personalized questions
    name: '',
    birthYear: '',
    hometown: '',
    currentLocation: '',
    jobs: '',
    notes: '',
    familyInfo: '',
    // Story type - NEW APPROACH
    storyType: STORY_TYPES.INDIVIDUAL,
    // Legacy fields (for backward compatibility)
    isGiftStory: false,
    recipientName: '',
    recipientEmail: '',
    purchaserName: '',
    relationship: '',
    // Collaborative story fields
    isCollaborativeStory: false,
    collaboratorName: '',
    collaboratorEmail: '',
    collaboratorRelationship: '',
    // Gift context field
    giftContext: '',
    // Story direction for collaborative stories
    storyDirection: ''
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showUpgradeInfo, setShowUpgradeInfo] = useState(false);

  // Helper function to update story type and sync legacy fields
  const updateStoryType = (newStoryType) => {
    const legacyFlags = StoryTypeUtils.isCollaborativeType(newStoryType) 
      ? { isCollaborativeStory: true, isGiftStory: StoryTypeUtils.isGiftType(newStoryType) }
      : { isCollaborativeStory: false, isGiftStory: StoryTypeUtils.isGiftType(newStoryType) };

    setFormData(prev => ({
      ...prev,
      storyType: newStoryType,
      ...legacyFlags
    }));
  };

  // Get current story type info for UI display
  const currentStoryInfo = {
    type: formData.storyType,
    label: STORY_TYPE_LABELS[formData.storyType],
    isCollaborative: StoryTypeUtils.isCollaborativeType(formData.storyType),
    isGift: StoryTypeUtils.isGiftType(formData.storyType),
    isIndividual: StoryTypeUtils.isIndividualType(formData.storyType)
  };

  // Detect user's location and set currency
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const country = data.country_code;
        
        const currencyMap = {
          'US': { code: 'USD', symbol: '$', forOnePrice: 76.50, forTwoPrice: 97.50, originalForOnePrice: 102, originalForTwoPrice: 130 },
          'AU': { code: 'AUD', symbol: '$', forOnePrice: 116.25, forTwoPrice: 148.50, originalForOnePrice: 155, originalForTwoPrice: 198 },
          'GB': { code: 'GBP', symbol: '£', forOnePrice: 60, forTwoPrice: 76.50, originalForOnePrice: 80, originalForTwoPrice: 102 },
          'NZ': { code: 'NZD', symbol: '$', forOnePrice: 134.96, forTwoPrice: 172.46, originalForOnePrice: 179.95, originalForTwoPrice: 229.95 },
        };
        
        setUserCurrency(currencyMap[country] || currencyMap['NZ']);
      } catch (error) {
        console.error('Failed to detect location:', error);
        // Default to NZD
        setUserCurrency({ code: 'NZD', symbol: '$', forOnePrice: 134.96, forTwoPrice: 172.46, originalForOnePrice: 179.95, originalForTwoPrice: 229.95 });
      }
    };
    
    detectCurrency();
  }, []);

  // Load user profile data to auto-populate fields
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!currentUser) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const profileData = userDoc.data();
          
          // Auto-populate form with profile data ONLY for individual, non-gift stories
          setFormData(prev => {
            // Don't auto-populate if it's a gift story or collaborative story
            if (StoryTypeUtils.isGiftType(prev.storyType) || StoryTypeUtils.isCollaborativeType(prev.storyType)) {
              return prev;
            }
            
            return {
              ...prev,
              // Only auto-populate if current form values are empty and it's a personal story
              name: prev.name || (profileData.displayName || ''),
              birthYear: prev.birthYear || (profileData.birthYear || ''),
              hometown: prev.hometown || (profileData.hometown || ''),
              currentLocation: prev.currentLocation || (profileData.currentLocation || ''),
              jobs: prev.jobs || (profileData.occupation || ''), // Map occupation -> jobs
              notes: prev.notes || (profileData.interests || ''), // Map interests -> notes
              familyInfo: prev.familyInfo || (profileData.familyInfo || '') // Map familyInfo -> familyInfo
            };
          });
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, [currentUser]);

  // Clear detailed personal fields when collaborative story is selected
  useEffect(() => {
    if (StoryTypeUtils.isCollaborativeType(formData.storyType)) {
      setFormData(prev => ({
        ...prev,
        // Clear personal details for collaborative stories
        birthYear: '',
        hometown: '',
        currentLocation: '',
        jobs: '',
        notes: '',
        familyInfo: '',
        additionalContext: '',
        // Clear name field for collaborative GIFT stories (since it should be recipient's name, not purchaser's)
        name: StoryTypeUtils.isGiftType(prev.storyType) ? '' : prev.name
      }));
    }
  }, [formData.storyType]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Allow both gift stories and collaborative stories to be selected together
      // This enables gifting collaborative stories (e.g., kids gifting to parents)
      
      return newData;
    });
  };

  const nextStep = () => {
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleCreateStory = async () => {
    setLoading(true);

    try {
      // Real-time credit availability check before starting
      if (currentUser?.email) {
        const normalizedEmail = currentUser.email.trim().toLowerCase();
        const primaryRef = doc(db, 'userAccess', normalizedEmail);
        const legacyRef = currentUser.email !== normalizedEmail ? doc(db, 'userAccess', currentUser.email) : null;
        let userAccessSnapshot = await getDoc(primaryRef);

        if (!userAccessSnapshot.exists() && legacyRef) {
          userAccessSnapshot = await getDoc(legacyRef);
        }

        if (!userAccessSnapshot.exists()) {
          alert('You need story credits to create new stories. Purchase Memory Stream to get started!');
          setLoading(false);
          return;
        }
        
        const accessData = userAccessSnapshot.data();
        const remainingCredits = Math.max(0, 
          (accessData.storyTokens || 0) - (accessData.tokensUsed || 0)
        );
        
        // Check if user has collaborative product access for collaborative stories
        if (StoryTypeUtils.isCollaborativeType(formData.storyType) && !accessData.isCollaborativeProduct) {
          // Redirect to upgrade or purchase page
          if (accessData.hasAccess) {
            // Existing customer - show upgrade page
            alert('You need to upgrade to Memory Stream For Two to create collaborative stories with another person.');
            navigate('/upgrade');
          } else {
            // New customer - show purchase page
            alert('You need Memory Stream For Two to create collaborative stories. Please purchase the two-author version.');
            navigate('/purchase');
          }
          setLoading(false);
          return;
        }
        
        if (remainingCredits <= 0) {
          alert('You need story credits to create new stories. Purchase Memory Stream to get started!');
          setLoading(false);
          return;
        }
      } else {
        alert('You need to be logged in to create stories.');
        setLoading(false);
        return;
      }

      // Create the story document first (don't consume credit yet)
      const storyData = {
        title: formData.title,
        description: formData.description,
        giftContext: formData.giftContext, // Keep giftContext separate from description
        storyDirection: formData.storyDirection, // Story direction for collaborative stories
        additionalContext: formData.additionalContext,
        // NEW: Clear story type
        storyType: formData.storyType,
        // Legacy fields (for backward compatibility)
        isGiftStory: StoryTypeUtils.isGiftType(formData.storyType),
        recipientName: formData.recipientName,
        recipientEmail: formData.recipientEmail,
        purchaserName: formData.purchaserName,
        relationship: formData.relationship,
        // Collaborative story fields
        isCollaborativeStory: StoryTypeUtils.isCollaborativeType(formData.storyType),
        collaborators: StoryTypeUtils.isCollaborativeType(formData.storyType) ? [{
          name: formData.collaboratorName,
          email: formData.collaboratorEmail,
          relationship: formData.collaboratorRelationship,
          status: 'pending',
          invitedAt: new Date(), // Use regular Date instead of serverTimestamp() in arrays
          invitedBy: currentUser.uid,
          userId: null // Will be set when they accept invitation
        }] : [],
        primaryAuthor: {
          userId: currentUser.uid,
          name: currentUser.displayName || formData.name || 'Primary Author',
          email: currentUser.email
        },
        name: formData.name,
        birthYear: formData.birthYear ? parseInt(formData.birthYear) : null,
        hometown: formData.hometown,
        currentLocation: formData.currentLocation,
        jobs: formData.jobs,
        notes: formData.notes,
        familyInfo: formData.familyInfo,
        // NEW: For gift stories, try to set recipient as owner immediately
        userId: StoryTypeUtils.isGiftType(formData.storyType) ? null : currentUser.uid, // Will be set in Cloud Function for gifts
        createdAt: new Date(), // Use regular Date for now to avoid serverTimestamp issues
        updatedAt: new Date(), // Use regular Date for now to avoid serverTimestamp issues
        questionCount: 0,
        // Track credit usage (will be set to true after successful creation)
        tokenConsumed: false,
        purchaseOrderId: userAccess?.orderId || null
      };

      // Add giftAccess only for gift stories to avoid undefined field issue
      if (StoryTypeUtils.isGiftType(formData.storyType)) {
        storyData.giftAccess = {
          recipientEmail: formData.recipientEmail,
          recipientName: formData.recipientName,
          gifter: {
            userId: currentUser.uid,
            name: formData.purchaserName || currentUser.displayName || 'Gift Giver',
            email: currentUser.email,
            relationship: formData.relationship
          },
          helpers: [], // Array for additional family members who can help
          pendingTransfer: true // Will be false once recipient account is found/created
        };
      }

      // Defensive check - ensure giftAccess is never undefined
      if (storyData.hasOwnProperty('giftAccess') && storyData.giftAccess === undefined) {
        delete storyData.giftAccess;
        console.warn('Removed undefined giftAccess field from story data');
      }

      console.log('Story data being created:', storyData);
      console.log('Story type:', formData.storyType);
      console.log('Is gift type?', StoryTypeUtils.isGiftType(formData.storyType));
      console.log('Has giftAccess field?', storyData.hasOwnProperty('giftAccess'));

      const storyRef = await addDoc(collection(db, 'stories'), storyData);

      // Generate questions based on story type - CLEAN NEW APPROACH
      let questionsResult;
      const storyType = formData.storyType;
      
      // Validate story type requirements
      const validationErrors = StoryTypeUtils.validateStoryTypeData(storyType, formData);
      if (validationErrors.length > 0) {
        alert('Please fix the following errors:\n' + validationErrors.join('\n'));
        setLoading(false);
        return;
      }
      
      console.log(`Creating ${STORY_TYPE_LABELS[storyType]} with story type: ${storyType}`);
      
      // Generate questions based on clear story type
      switch (storyType) {
        case STORY_TYPES.INDIVIDUAL:
          // Individual personal story
          questionsResult = await fn.generateEnhancedStoryPrompts({
            topic: formData.description,
            name: formData.name,
            birthYear: formData.birthYear ? parseInt(formData.birthYear) : null,
            hometown: formData.hometown,
            currentLocation: formData.currentLocation,
            jobs: formData.jobs,
            notes: formData.notes,
            familyInfo: formData.familyInfo,
            additionalNotes: formData.additionalContext,
            currentYear: new Date().getFullYear()
          });
          break;
          
        case STORY_TYPES.INDIVIDUAL_GIFT:
          // Individual story given as a gift
          questionsResult = await fn.generateEnhancedStoryPrompts({
            topic: formData.giftContext || formData.description, // Use giftContext for direction, fallback to description
            name: formData.recipientName || formData.name,
            birthYear: formData.birthYear ? parseInt(formData.birthYear) : null,
            hometown: formData.hometown,
            currentLocation: formData.currentLocation,
            jobs: formData.jobs,
            notes: formData.notes,
            familyInfo: formData.familyInfo,
            additionalNotes: formData.additionalContext,
            giftContext: formData.giftContext, // Pass gift context specifically for AI question generation
            recipientName: formData.recipientName,
            recipientEmail: formData.recipientEmail,
            purchaserName: formData.purchaserName,
            relationship: `The recipient is the gift giver's ${formData.relationship}`, // Add clarity for AI
            currentYear: new Date().getFullYear()
          });
          break;
          
        case STORY_TYPES.COLLABORATIVE:
          // Two people creating story together
          questionsResult = await fn.generateCollaborativeStoryPrompts({
            storyTitle: formData.title,
            relationshipType: formData.collaboratorRelationship || 'relationship',
            person1Name: formData.name || 'Person 1',
            person2Name: formData.collaboratorName || 'Person 2',
            isGiftStory: false,
            storyDirection: formData.storyDirection || '' // Add story direction for tone/question guidance
          });
          break;
          
        case STORY_TYPES.COLLABORATIVE_GIFT:
          // Collaborative story given as a gift
          questionsResult = await fn.generateCollaborativeStoryPrompts({
            storyTitle: formData.title,
            relationshipType: formData.collaboratorRelationship || 'relationship',
            person1Name: formData.name || 'Person 1',
            person2Name: formData.collaboratorName || 'Person 2',
            isGiftStory: true,
            recipientName: formData.recipientName || '',
            purchaserName: formData.purchaserName || '',
            giftRelationship: `The recipient is the gift giver's ${formData.relationship}`, // Add clarity for AI
            giftContext: formData.giftContext || ''
          });
          break;
          
        default:
          throw new Error(`Unknown story type: ${storyType}`);
      }

      // Add questions to the story
      const questionsCollection = collection(db, 'stories', storyRef.id, 'questions');
      
      const questions = questionsResult.data.questions || [];
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        const questionText = typeof question === 'string' ? question : question.text;
        const questionId = uuidv4();
        
        // Use setDoc with specific ID instead of addDoc
        await setDoc(doc(questionsCollection, questionId), {
          id: questionId,
          text: questionText,
          order: i,
          storyId: storyRef.id,
          userId: currentUser.uid,
          createdAt: serverTimestamp(),
          hasAnswer: false
        });
      }

      // Story and questions created successfully - now consume the credit
      if (userAccess && currentUser?.email) {
        // Double-check credit availability before consumption
        const normalizedEmail = currentUser.email.trim().toLowerCase();
        const primaryRef = doc(db, 'userAccess', normalizedEmail);
        const legacyRef = currentUser.email !== normalizedEmail ? doc(db, 'userAccess', currentUser.email) : null;
        let userAccessSnapshot = await getDoc(primaryRef);

        if (!userAccessSnapshot.exists() && legacyRef) {
          userAccessSnapshot = await getDoc(legacyRef);
        }
        
        if (userAccessSnapshot.exists()) {
          const currentAccessData = userAccessSnapshot.data();
          const currentRemainingCredits = Math.max(0, 
            (currentAccessData.storyTokens || 0) - (currentAccessData.tokensUsed || 0)
          );
          
          if (currentRemainingCredits > 0) {
            // Consume the credit
            await updateDoc(userAccessSnapshot.ref, {
              tokensUsed: (currentAccessData.tokensUsed || 0) + 1,
              lastTokenUsedAt: serverTimestamp()
            });

            // Mark the story as having consumed a token
            await updateDoc(doc(db, 'stories', storyRef.id), {
              tokenConsumed: true
            });
          } else {
            // This shouldn't happen, but handle it gracefully
            console.warn('No credits available at consumption time');
            await updateDoc(doc(db, 'stories', storyRef.id), {
              tokenConsumed: false,
              createdWithoutToken: true
            });
          }
        }
      }

      // Send collaboration invitation if this is a collaborative story
      if (formData.isCollaborativeStory && formData.collaboratorEmail) {
        try {
          console.log('📧 Sending collaboration invitation...');
          await fn.sendCollaboratorInvitation({
            storyId: storyRef.id,
            collaboratorEmail: formData.collaboratorEmail,
            collaboratorName: formData.collaboratorName,
            storyTitle: formData.title,
            inviterName: currentUser.displayName || formData.name || 'A Memory Stream user'
          });
          console.log('✅ Collaboration invitation sent successfully');
        } catch (error) {
          console.error('Error sending collaboration invitation:', error);
          // Don't fail the entire story creation if invitation fails
          alert('Story created successfully, but there was an issue sending the collaboration invitation. You can resend it later.');
        }
      }

      // Send gift story notification if this is a gift story
      if (formData.isGiftStory && formData.recipientEmail) {
        try {
          console.log('🎁 Sending gift story notification...');
          await fn.sendGiftStoryNotification({
            storyId: storyRef.id,
            recipientEmail: formData.recipientEmail,
            recipientName: formData.recipientName,
            giftGiverName: formData.purchaserName || currentUser.displayName || 'Someone special',
            storyTitle: formData.title,
            storyType: formData.storyType,
            relationship: formData.relationship,
            // 🆕 Pass second collaborator info for automatic access granting
            secondCollaboratorEmail: formData.collaboratorEmail,
            secondCollaboratorName: formData.collaboratorName
          });
          console.log('✅ Gift story notification sent successfully');
        } catch (error) {
          console.error('Error sending gift story notification:', error);
          // Don't fail the entire story creation if notification fails
          alert('Story created successfully, but there was an issue sending the gift notification. The recipient can still access their story when they log in.');
        }
      }

      // Redirect to the appropriate editor based on story type
      if (StoryTypeUtils.isCollaborativeType(formData.storyType)) {
        navigate(`/collaborative-story/${storyRef.id}`);
      } else {
        navigate(`/story/${storyRef.id}`);
      }
    } catch (error) {
      console.error('Error creating story:', error);
      console.error('Full error details:', error.message, error.code, error.details);
      alert(`Failed to create story: ${error.message || 'Unknown error'}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="memory-stream-card">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Create New Story</h1>
              <p className="text-gray-600">
                {step === 1 && "Tell us about the story you'd like to capture."}
                {step === 2 && !formData.isCollaborativeStory && "Help us personalize your questions with some background information."}
                {step === 2 && formData.isCollaborativeStory && "Tell us about your collaborative story."}
                {step === 3 && "Review your story details before we generate personalized questions."}
              </p>
            </div>
            {userAccess && (
              <div className="text-right">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <div className="text-sm font-medium text-gray-700">Credits Remaining</div>
                  <div className="text-2xl font-bold text-blue-600">{remainingTokens}</div>
                  <div className="text-xs text-gray-500">of {userAccess.storyTokens}</div>
                  {userAccess?.isCollaborativeProduct && (
                    <div className="text-xs text-blue-600 mt-1">
                      Each story uses 1 credit (individual or collaborative)
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* No Credits Purchase Section */}
          {userAccess && remainingTokens <= 0 && (
            <div className="mt-6 p-6 border-2 border-dashed border-[#C87400] rounded-lg bg-[#F0E6D2]">
              <div className="text-center">
                <h3 className="text-lg font-bold text-[#061D3D] mb-2">No Story Credits Remaining</h3>
                <p className="text-gray-700 mb-6">
                  You need story credits to create new stories. Purchase Memory Stream to continue:
                </p>
                
                <div className="max-w-md mx-auto">
                  {/* Memory Stream For One */}
                  <div className="bg-white rounded-lg p-6 border-2 border-[#C87400] shadow-sm">
                    <h4 className="font-bold text-[#061D3D] text-xl mb-2">Memory Stream</h4>
                    <div className="mb-4">
                      <span className="text-2xl font-bold text-[#C87400]">
                        {userCurrency.symbol}{userCurrency.forOnePrice} {userCurrency.code}
                      </span>
                      <span className="text-sm text-[#C87400] ml-2">
                        (25% off)
                      </span>
                      <div className="text-xs text-gray-500 line-through">
                        Regular: {userCurrency.symbol}{userCurrency.originalForOnePrice}
                      </div>
                    </div>
                    <ul className="text-sm text-gray-600 text-left space-y-2 mb-6">
                      <li>• 1 Personal Memory Story</li>
                      <li>• Personalised Questions</li>
                      <li>• Professional PDF Export</li>
                      <li>• Physical Printed Book</li>
                    </ul>
                    <button
                      onClick={async () => {
                        setLoading(true);
                        try {
                          const idToken = await currentUser.getIdToken();
                          const response = await fetch('https://us-central1-memory-stream-12e9d.cloudfunctions.net/createStripeCheckout', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${idToken}`
                            },
                            body: JSON.stringify({
                              productId: 'memory-stream-for-one',
                              customerEmail: currentUser.email,
                              customerName: currentUser.displayName || currentUser.email,
                              isUpgrade: false,
                              successUrl: `${window.location.origin}/story/new`,
                              cancelUrl: `${window.location.origin}/story/new`
                            })
                          });
                          
                          if (!response.ok) throw new Error('Failed to create checkout');
                          const data = await response.json();
                          window.location.href = data.checkoutUrl;
                        } catch (error) {
                          console.error('Error:', error);
                          alert('Failed to start checkout. Please try again.');
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="w-full px-6 py-3 bg-[#061D3D] hover:bg-[#0A2A52] text-white font-medium rounded-lg transition-colors disabled:opacity-50 text-lg"
                    >
                      {loading ? 'Loading...' : 'Purchase Now'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <div className="flex space-x-2">
              {[1, 2, 3].map((stepNum) => (
                <div
                  key={stepNum}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    stepNum <= step 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {stepNum}
                </div>
              ))}
            </div>
            <span className="text-sm text-gray-500">Step {step} of 3</span>
          </div>
        </div>

        <form className="space-y-6">{/* Removed onSubmit to prevent auto-submission */}
          {/* Step 1: Basic Story Info */}
          {step === 1 && (
            <>
              {/* Story Type Selector - NEW CLEAN APPROACH */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Story Type
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Individual Story */}
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.storyType === STORY_TYPES.INDIVIDUAL 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => updateStoryType(STORY_TYPES.INDIVIDUAL)}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="storyType"
                        value={STORY_TYPES.INDIVIDUAL}
                        checked={formData.storyType === STORY_TYPES.INDIVIDUAL}
                        onChange={() => updateStoryType(STORY_TYPES.INDIVIDUAL)}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <h4 className="font-medium text-gray-900">{STORY_TYPE_LABELS[STORY_TYPES.INDIVIDUAL]}</h4>
                        <p className="text-sm text-gray-500">Create your personal life story</p>
                      </div>
                    </div>
                  </div>

                  {/* Individual Gift Story */}
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.storyType === STORY_TYPES.INDIVIDUAL_GIFT 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => updateStoryType(STORY_TYPES.INDIVIDUAL_GIFT)}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="storyType"
                        value={STORY_TYPES.INDIVIDUAL_GIFT}
                        checked={formData.storyType === STORY_TYPES.INDIVIDUAL_GIFT}
                        onChange={() => updateStoryType(STORY_TYPES.INDIVIDUAL_GIFT)}
                        className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500"
                      />
                      <div>
                        <h4 className="font-medium text-gray-900">{STORY_TYPE_LABELS[STORY_TYPES.INDIVIDUAL_GIFT]}</h4>
                        <p className="text-sm text-gray-500">Gift a personal story to someone special</p>
                      </div>
                    </div>
                  </div>

                  {/* Collaborative Story */}
                  {userAccess?.isCollaborativeProduct && (
                    <div 
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.storyType === STORY_TYPES.COLLABORATIVE 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onClick={() => updateStoryType(STORY_TYPES.COLLABORATIVE)}
                    >
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="storyType"
                          value={STORY_TYPES.COLLABORATIVE}
                          checked={formData.storyType === STORY_TYPES.COLLABORATIVE}
                          onChange={() => updateStoryType(STORY_TYPES.COLLABORATIVE)}
                          className="mr-3 h-4 w-4 text-orange-600 focus:ring-orange-500"
                        />
                        <div>
                          <h4 className="font-medium text-gray-900">{STORY_TYPE_LABELS[STORY_TYPES.COLLABORATIVE]}</h4>
                          <p className="text-sm text-gray-500">Create a shared story with another author</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Collaborative Gift Story */}
                  {userAccess?.isCollaborativeProduct && (
                    <div 
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.storyType === STORY_TYPES.COLLABORATIVE_GIFT 
                          ? 'border-pink-500 bg-pink-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onClick={() => updateStoryType(STORY_TYPES.COLLABORATIVE_GIFT)}
                    >
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="storyType"
                          value={STORY_TYPES.COLLABORATIVE_GIFT}
                          checked={formData.storyType === STORY_TYPES.COLLABORATIVE_GIFT}
                          onChange={() => updateStoryType(STORY_TYPES.COLLABORATIVE_GIFT)}
                          className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500"
                        />
                        <div>
                          <h4 className="font-medium text-gray-900">{STORY_TYPE_LABELS[STORY_TYPES.COLLABORATIVE_GIFT]}</h4>
                          <p className="text-sm text-gray-500">Gift a collaborative story experience for two people</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Legacy Checkboxes - KEEPING FOR BACKWARD COMPATIBILITY BUT HIDDEN */}
              <div style={{ display: 'none' }}>
                <input
                  type="checkbox"
                  name="isGiftStory"
                  checked={currentStoryInfo.isGift}
                  readOnly
                />
                <input
                  type="checkbox"
                  name="isCollaborativeStory"
                  checked={currentStoryInfo.isCollaborative}
                  readOnly
                />
              </div>

              {/* Collaborative Gift Story Info */}
              {formData.storyType === STORY_TYPES.COLLABORATIVE_GIFT && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-3 mb-4">
                  <div className="flex items-center mb-2">
                    <span className="text-lg">🎁👥</span>
                    <h4 className="text-sm font-medium text-purple-800 ml-2">Collaborative Gift Story</h4>
                  </div>
                  <p className="text-sm text-purple-700">
                    Perfect! You're gifting a Memory Stream For Two experience to two specific people. 
                    Both authors will receive invitations and can work together to create their shared story.
                  </p>
                </div>
              )}

              {/* Gift Story Fields */}
              {StoryTypeUtils.isGiftType(formData.storyType) && (
                <div className="bg-blue-50 p-4 rounded-md space-y-4">
                  <div>
                    <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700 mb-2">
                      {StoryTypeUtils.isCollaborativeType(formData.storyType) ? 
                        "Primary Author's Name" : 
                        "Recipient's Name"
                      }
                    </label>
                    <input
                      type="text"
                      id="recipientName"
                      name="recipientName"
                      value={formData.recipientName}
                      onChange={handleInputChange}
                      placeholder={StoryTypeUtils.isCollaborativeType(formData.storyType) ? 
                        "Primary author's name (e.g., Sarah)" : 
                        "Recipient's Name"
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required={StoryTypeUtils.isGiftType(formData.storyType)}
                    />
                  </div>
                  <div>
                    <label htmlFor="recipientEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      {StoryTypeUtils.isCollaborativeType(formData.storyType) ? 
                        "Primary Author's Email Address" : 
                        "Recipient's Email Address"
                      }
                    </label>
                    <input
                      type="email"
                      id="recipientEmail"
                      name="recipientEmail"
                      value={formData.recipientEmail}
                      onChange={handleInputChange}
                      placeholder="e.g., sarah@email.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required={StoryTypeUtils.isGiftType(formData.storyType)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {StoryTypeUtils.isCollaborativeType(formData.storyType) ? 
                        "They'll use this email to access their collaborative story and invite their co-author" :
                        "They'll use this email to access their story when they create an account"
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="purchaserName" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name
                      </label>
                      <input
                        type="text"
                        id="purchaserName"
                        name="purchaserName"
                        value={formData.purchaserName}
                        onChange={handleInputChange}
                        placeholder="Gift Giver's Name/s"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 mb-2">
                        {formData.isCollaborativeStory ? 
                          "Your Relationship to Primary Author" : 
                          "The recipient is my..."
                        }
                      </label>
                      <input
                        type="text"
                        id="relationship"
                        name="relationship"
                        value={formData.relationship}
                        onChange={handleInputChange}
                        placeholder={StoryTypeUtils.isCollaborativeType(formData.storyType) ? 
                          "They are my... (e.g., Father, Mother, Sister)" : 
                          "e.g., daughter, son, mother, father, friend"
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  {/* Gift Context Field - Story direction */}
                  <div>
                    <label htmlFor="giftContext" className="block text-sm font-medium text-gray-700 mb-2">
                      Story direction
                    </label>
                    <textarea
                      id="giftContext"
                      name="giftContext"
                      value={formData.giftContext}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Example: My daughter might like to learn more about her grandparents growing up together..."
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Help us create more personalized questions by sharing what the gift recipient might want to learn about.
                    </p>
                  </div>
                </div>
              )}

              {formData.isCollaborativeStory && (
                <div className="bg-orange-50 p-4 rounded-md space-y-4">
                  <div className="flex items-center mb-3">
                    <span className="text-lg">👥</span>
                    <h3 className="text-lg font-medium text-gray-900 ml-2">
                      {formData.isGiftStory ? 'Collaborative Gift Story Details' : 'Collaborative Story Details'}
                    </h3>
                  </div>
                  {formData.isGiftStory && (
                    <div className="bg-white border border-orange-200 rounded p-3 mb-4">
                      <p className="text-sm text-orange-800">
                        <strong>Collaborative Gift:</strong> You're setting up a collaborative story for two specific people. 
                        Both the recipient and the second author will receive invitations to work together on their shared story.
                      </p>
                    </div>
                  )}
                  <div>
                    <label htmlFor="collaboratorName" className="block text-sm font-medium text-gray-700 mb-2">
                      Second Author's Name
                    </label>
                    <input
                      type="text"
                      id="collaboratorName"
                      name="collaboratorName"
                      value={formData.collaboratorName}
                      onChange={handleInputChange}
                      placeholder={formData.isGiftStory ? 
                        "Who will the recipient collaborate with?" : 
                        "Who will co-author this story with you?"
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required={formData.isCollaborativeStory}
                    />
                  </div>
                  <div>
                    <label htmlFor="collaboratorEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Second Author's Email Address
                    </label>
                    <input
                      type="email"
                      id="collaboratorEmail"
                      name="collaboratorEmail"
                      value={formData.collaboratorEmail}
                      onChange={handleInputChange}
                      placeholder="e.g., john@email.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required={formData.isCollaborativeStory}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.isGiftStory ? 
                        "Both the recipient and second author will receive invitations to collaborate" :
                        "They'll receive an invitation to collaborate on this story"
                      }
                    </p>
                  </div>
                  <div>
                    <label htmlFor="collaboratorRelationship" className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.isGiftStory ? 'Their Relationship to Recipient' : 'Your Relationship'}
                    </label>
                    <input
                      type="text"
                      id="collaboratorRelationship"
                      name="collaboratorRelationship"
                      value={formData.collaboratorRelationship}
                      onChange={handleInputChange}
                      placeholder={formData.isGiftStory ? 
                        "e.g., spouse, sibling, parent, best friend" :
                        "e.g., spouse, sibling, best friend, business partner"
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  
                  {/* Story Direction Field for non-gift collaborative stories */}
                  {!formData.isGiftStory && (
                    <div>
                      <label htmlFor="storyDirection" className="block text-sm font-medium text-gray-700 mb-2">
                        Story direction
                      </label>
                      <textarea
                        id="storyDirection"
                        name="storyDirection"
                        value={formData.storyDirection}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Example: We'd like to focus on our shared adventures and how our friendship has grown over the years..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Optional: Help set the tone for the story and the direction of the questions
                      </p>
                    </div>
                  )}
                  <div className="bg-orange-100 p-3 rounded-md">
                    <p className="text-sm text-orange-800">
                      <strong>How it works:</strong> {formData.isGiftStory ? 
                        'The recipient will receive an invitation to start their collaborative story. They can then invite their collaborator to join them in answering questions and sharing memories together.' :
                        'You\'ll create the story together, taking turns answering questions and sharing memories. Both authors can record answers and both names will appear on the final story.'
                      }
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Story Title
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder={formData.isGiftStory ? `${formData.recipientName || "Their"} Life Story` : "My Journey to College"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Story Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Briefly describe what this story is about... (e.g., Life Story, Career Journey, Family History)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </>
          )}

          {/* Step 2: Personal Details OR Collaborative Details */}
          {step === 2 && !formData.isCollaborativeStory && (
            <>
              {/* Smart Auto-Fill message - Only for personal (non-gift) stories */}
              {!StoryTypeUtils.isGiftType(formData.storyType) && (
                <div className="bg-amber-50 p-4 rounded-md mb-6">
                  <h3 className="text-sm font-medium text-amber-800 mb-2">💡 Smart Auto-Fill</h3>
                  <p className="text-sm text-amber-700">
                    We've pre-filled these fields with information from your profile to save you time. 
                    Feel free to modify them for this specific story or leave them as-is.
                  </p>
                </div>
              )}

              {/* Gift Story message - Only for gift stories */}
              {StoryTypeUtils.isGiftType(formData.storyType) && (
                <div className="bg-purple-50 p-4 rounded-md mb-6">
                  <h3 className="text-sm font-medium text-purple-800 mb-2">🎁 Gift Story Details</h3>
                  <p className="text-sm text-purple-700">
                    Please provide details about the person whose story this will be. This information helps us create personalized questions that will capture their unique life experiences.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.isGiftStory ? "Recipient's Name" : "Your Name"}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={formData.isGiftStory ? "The person whose story this is" : "Your full name"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="birthYear" className="block text-sm font-medium text-gray-700 mb-2">
                    Birth Year
                  </label>
                  <input
                    type="number"
                    id="birthYear"
                    name="birthYear"
                    value={formData.birthYear}
                    onChange={handleInputChange}
                    placeholder="e.g., 1965"
                    min="1900"
                    max={new Date().getFullYear()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Helps generate era-specific questions</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="hometown" className="block text-sm font-medium text-gray-700 mb-2">
                    Hometown
                  </label>
                  <input
                    type="text"
                    id="hometown"
                    name="hometown"
                    value={formData.hometown}
                    onChange={handleInputChange}
                    placeholder="e.g., Chicago, IL"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Where they grew up</p>
                </div>
                <div>
                  <label htmlFor="currentLocation" className="block text-sm font-medium text-gray-700 mb-2">
                    Currently Living In
                  </label>
                  <input
                    type="text"
                    id="currentLocation"
                    name="currentLocation"
                    value={formData.currentLocation}
                    onChange={handleInputChange}
                    placeholder="e.g., Seattle, WA"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">For current life and location-specific memories</p>
                </div>
              </div>

              <div>
                <label htmlFor="jobs" className="block text-sm font-medium text-gray-700 mb-2">
                  Career/Occupation History
                </label>
                <textarea
                  id="jobs"
                  name="jobs"
                  value={formData.jobs}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="e.g., Teacher at Roosevelt Elementary (1985-2010), then freelance tutoring. Engineering roles at Boeing and Microsoft..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Include main careers or jobs throughout life with details and timeframes</p>
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Context & Interests
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Hobbies, interests, life experiences, or anything else that might help create better questions..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="familyInfo" className="block text-sm font-medium text-gray-700 mb-2">
                  Family Information
                </label>
                <textarea
                  id="familyInfo"
                  name="familyInfo"
                  value={formData.familyInfo}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Family members, relationships, traditions, spouse, children, parents, siblings..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Helps generate questions about family relationships and traditions</p>
              </div>

              <div>
                <label htmlFor="additionalContext" className="block text-sm font-medium text-gray-700 mb-2">
                  Story Focus Areas (Optional)
                </label>
                <textarea
                  id="additionalContext"
                  name="additionalContext"
                  value={formData.additionalContext}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Any specific time periods, themes, or aspects you'd like the questions to emphasize..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {/* Step 2: Collaborative Story Details */}
          {step === 2 && formData.isCollaborativeStory && (
            <>
              <div className="bg-orange-50 p-4 rounded-md mb-6">
                <h3 className="text-sm font-medium text-orange-800 mb-2">👥 Collaborative Story Setup</h3>
                <p className="text-sm text-orange-700">
                  For collaborative stories, we create universal questions that both people can answer from their own perspective. 
                  No need for specific personal details - just tell us about your collaboration. The questions will become more personalized as you progress with our smart follow-up questions function.
                </p>
              </div>

              {!formData.isGiftStory && (
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              )}

              {formData.isGiftStory && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      First Author's Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="First person's name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="collaboratorName" className="block text-sm font-medium text-gray-700 mb-2">
                      Second Author's Name
                    </label>
                    <input
                      type="text"
                      id="collaboratorName"
                      name="collaboratorName"
                      value={formData.collaboratorName}
                      onChange={handleInputChange}
                      placeholder="Second person's name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required={formData.isCollaborativeStory}
                    />
                  </div>
                </div>
              )}

              {!formData.isGiftStory && (
                <div>
                  <label htmlFor="collaboratorName" className="block text-sm font-medium text-gray-700 mb-2">
                    Co-Author's Name
                  </label>
                  <input
                    type="text"
                    id="collaboratorName"
                    name="collaboratorName"
                    value={formData.collaboratorName}
                    onChange={handleInputChange}
                    placeholder="Who will co-author this story with you?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required={formData.isCollaborativeStory}
                  />
                </div>
              )}

              <div>
                <label htmlFor="collaboratorEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Co-Author's Email Address
                </label>
                <input
                  type="email"
                  id="collaboratorEmail"
                  name="collaboratorEmail"
                  value={formData.collaboratorEmail}
                  onChange={handleInputChange}
                  placeholder="e.g., john@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required={formData.isCollaborativeStory}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.isGiftStory ? 
                    "Both authors will receive invitations to collaborate" :
                    "They'll receive an invitation to collaborate on this story"
                  }
                </p>
              </div>

              <div>
                <label htmlFor="collaboratorRelationship" className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.isGiftStory ? 'Their Relationship' : 'Your Relationship'}
                </label>
                <input
                  type="text"
                  id="collaboratorRelationship"
                  name="collaboratorRelationship"
                  value={formData.collaboratorRelationship}
                  onChange={handleInputChange}
                  placeholder={formData.isGiftStory ? 
                    "e.g., married couple, siblings, best friends, parent and child" :
                    "e.g., They are my wife"
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">Helps us create appropriate questions for your relationship dynamic</p>
              </div>

              <div className="bg-orange-100 p-4 rounded-md">
                <h4 className="text-sm font-medium text-orange-800 mb-2">✨ How Collaborative Stories Work</h4>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Universal questions that both people can answer</li>
                  <li>• Each person shares their own perspective on the same topics</li>
                  <li>• Creates a rich, multi-dimensional story</li>
                  <li>• Perfect for capturing shared experiences and different viewpoints</li>
                </ul>
              </div>
            </>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-amber-50 p-4 rounded-md mb-6">
                <h3 className="text-sm font-medium text-amber-800 mb-2">📋 Review Your Story Details</h3>
                <p className="text-sm text-amber-700">
                  Please review all the information below. You can go back to make changes, or continue to create your story and generate personalized questions.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="font-medium text-gray-900 mb-3">Story Summary</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Title:</dt>
                    <dd className="text-sm text-gray-900">{formData.title}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Description:</dt>
                    <dd className="text-sm text-gray-900">{formData.description}</dd>
                  </div>
                  {formData.isGiftStory && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Gift Story:</dt>
                      <dd className="text-sm text-gray-900">
                        From {formData.purchaserName} to {formData.recipientName} ({formData.relationship})
                      </dd>
                    </div>
                  )}
                  {formData.isGiftStory && formData.recipientEmail && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Recipient Email:</dt>
                      <dd className="text-sm text-gray-900">{formData.recipientEmail}</dd>
                    </div>
                  )}
                  {formData.isCollaborativeStory && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Collaborative Story:</dt>
                      <dd className="text-sm text-gray-900">
                        👥 Co-authored with {formData.collaboratorName}
                        {formData.collaboratorRelationship && ` (${formData.collaboratorRelationship})`}
                      </dd>
                    </div>
                  )}
                  {formData.isCollaborativeStory && formData.collaboratorEmail && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Collaborator Email:</dt>
                      <dd className="text-sm text-gray-900">{formData.collaboratorEmail}</dd>
                    </div>
                  )}
                  {formData.isCollaborativeStory && formData.storyDirection && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Story Direction:</dt>
                      <dd className="text-sm text-gray-900">{formData.storyDirection}</dd>
                    </div>
                  )}
                  {/* Show Authors section for collaborative stories */}
                  {formData.isCollaborativeStory && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Authors:</dt>
                      <dd className="text-sm text-gray-900">
                        <div>
                          {formData.name || 'Primary Author'}
                          {formData.birthYear && ` (born ${formData.birthYear})`}
                          {formData.hometown && ` from ${formData.hometown}`}
                          {formData.currentLocation && formData.currentLocation !== formData.hometown && `, now in ${formData.currentLocation}`}
                        </div>
                        <div className="mt-1">
                          {formData.collaboratorName} ({formData.collaboratorEmail})
                        </div>
                      </dd>
                    </div>
                  )}
                  {/* Show Person section only for non-collaborative stories */}
                  {!formData.isCollaborativeStory && formData.name && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Person:</dt>
                      <dd className="text-sm text-gray-900">
                        {formData.name}
                        {formData.birthYear && ` (born ${formData.birthYear})`}
                        {formData.hometown && ` from ${formData.hometown}`}
                        {formData.currentLocation && formData.currentLocation !== formData.hometown && `, now in ${formData.currentLocation}`}
                      </dd>
                    </div>
                  )}
                  {formData.jobs && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Career:</dt>
                      <dd className="text-sm text-gray-900">{formData.jobs}</dd>
                    </div>
                  )}
                  {formData.notes && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Additional Context & Interests:</dt>
                      <dd className="text-sm text-gray-900">{formData.notes}</dd>
                    </div>
                  )}
                  {formData.familyInfo && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Family Information:</dt>
                      <dd className="text-sm text-gray-900">{formData.familyInfo}</dd>
                    </div>
                  )}
                  {formData.additionalContext && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Story Focus Areas:</dt>
                      <dd className="text-sm text-gray-900">{formData.additionalContext}</dd>
                    </div>
                  )}
                </dl>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-md">
                <h3 className="font-medium text-blue-900 mb-2">🎯 What happens next?</h3>
                <p className="text-sm text-blue-800">
                  When you click "Confirm & Create Story", we'll use this information to generate 25 highly personalized questions that:
                </p>
                <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
                  <li>Reference specific historical events and cultural moments from their era</li>
                  <li>Include location-specific memories and regional experiences</li>
                  <li>Balance personal growth, relationships, and career experiences</li>
                  <li>Create opportunities for rich, detailed storytelling</li>
                </ul>
                <p className="text-sm text-blue-800 mt-3 font-medium">
                  💡 You can always go back and make changes before confirming.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between space-x-4">
            <div>
              {step > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="memory-stream-button-secondary"
                  disabled={loading}
                >
                  ← Previous
                </button>
              )}
            </div>
            
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="memory-stream-button-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              
              {step < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="memory-stream-button"
                  disabled={loading || (step === 1 && (!formData.title || !formData.description || 
                    (formData.isGiftStory && (!formData.recipientName || !formData.recipientEmail)) ||
                    (formData.isCollaborativeStory && (!formData.collaboratorName || !formData.collaboratorEmail))
                  ))}
                >
                  Next →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCreateStory}
                  className="memory-stream-button"
                  disabled={loading}
                >
                  {loading ? 'Creating Story...' : '✅ Confirm & Create Story'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
